import { collection, getCountFromServer, getAggregateFromServer, sum, query, where } from "firebase/firestore";
import Failure from "../../../core/Failure";
import { FirestoreCollections, firestore } from "../../../firebase.config";
import { round2digits } from '../../../core/utils/commonUtils';
import countScheduledMessages from '../jobs/countScheduledMessages';

async function getOverviewStats(): Promise<{
  subscriptionsCount: number;
  contactsCount: number;
  engagedNotAvailableCount: number;
  usersCount: number;
  numbersCount: number;
  jobsCount: number;
  smsCount: number;
  totalPriceSms: number;
  replyRate: number;
  qualificationDistribution: {
    interested: number;
    considering: number;
    not_interested: number;
    not_responsive: number;
  };
}> {
  try {
    const db = firestore;
    const subscriptionsCollection = collection(
      db,
      FirestoreCollections.subscriptions
    );
    const contactsCollection = collection(db, FirestoreCollections.contacts);
    const numbersCollection = collection(db, FirestoreCollections.numbers);
    const usersCollection = collection(db, FirestoreCollections.users);
    const smsCollection = collection(db, FirestoreCollections.sms);

    // Run all queries in parallel
    const [
      subscriptionSnaphsot,
      contactsSnapshot,
      usersSnapshot,
      numbersSnapshot,
      scheduledMessagesCount,
      smsSnapshot,
      priceSmsSumSnapshotAssistant,
      priceSmsSumSnapshotUser,
      repliedContactsSnapshot,
      totalContactsSnapshop,
      engagedNotAvailableSnapshot

    ] = await Promise.all([
      getCountFromServer(subscriptionsCollection),
      getCountFromServer(contactsCollection),
      getCountFromServer(usersCollection),
      getCountFromServer(numbersCollection),
      countScheduledMessages(),
      getCountFromServer(smsCollection),
      // SMS COST
      getAggregateFromServer(smsCollection, { totalPriceSms: sum("price") }),
      getAggregateFromServer(smsCollection, { totalPriceSms: sum("price") }),
      getCountFromServer(query(contactsCollection, where("has_replied", "==", true))),
      getCountFromServer(query(contactsCollection, where("last_activity.role", "in", ["user", "assistant"]))),
      getCountFromServer(query(
        contactsCollection,
        where("last_activity.role", "in", ["user", "assistant"]),
        where("qualification_ai.qualification", "!=", "not_available")
      ))
    ]);

    // --------------------------------------------
    // ----------------- SMS COST -----------------
    // --------------------------------------------

    // Total
    const totalPrice = round2digits(priceSmsSumSnapshotAssistant.data().totalPriceSms) + round2digits(priceSmsSumSnapshotUser.data().totalPriceSms);

    // Calculate reply rate
    const totalContactsEngaged = totalContactsSnapshop.data().count ?? 0;
    const totalContacts = contactsSnapshot.data().count ?? 0;
    const repliedContacts = repliedContactsSnapshot.data().count ?? 0;
    const replyRate = totalContacts > 0 ? round2digits((repliedContacts / totalContactsEngaged) * 100) : 0;
    // ---

    // Requête pour obtenir les contacts engagés ET qui ne sont pas dans la catégorie "not_available"
    const engagedNotAvailableCount = engagedNotAvailableSnapshot.data().count ?? 0;
    // ------

    // Calculate Contacts interested, considering, not interested, not available
    const interestedQuery = query(contactsCollection, where("qualification_ai.qualification", "==", "interested"));
    const consideringQuery = query(contactsCollection, where("qualification_ai.qualification", "==", "considering"));
    const notInterestedQuery = query(contactsCollection, where("qualification_ai.qualification", "==", "not_interested"));
    const notResponsiveQuery = query(contactsCollection, where("qualification_ai.qualification", "==", "not_responsive"));

    const interestedCount = (await getCountFromServer(interestedQuery)).data().count || 0;
    const consideringCount = (await getCountFromServer(consideringQuery)).data().count || 0;
    const notInterestedCount = (await getCountFromServer(notInterestedQuery)).data().count || 0;
    const notResponsiveCount = (await getCountFromServer(notResponsiveQuery)).data().count || 0;
    // ---

    return {
      subscriptionsCount: subscriptionSnaphsot.data().count ?? 0,
      contactsCount: contactsSnapshot.data().count ?? 0,
      engagedNotAvailableCount,
      usersCount: usersSnapshot.data().count ?? 0,
      numbersCount: numbersSnapshot.data().count ?? 0,
      jobsCount: scheduledMessagesCount,
      smsCount: smsSnapshot.data().count ?? 0,
      totalPriceSms: round2digits(round2digits(totalPrice)) ?? 0,
      replyRate,
      qualificationDistribution: {
        interested: interestedCount,
        considering: consideringCount,
        not_interested: notInterestedCount,
        not_responsive: notResponsiveCount
      },
    };
  } catch (e) {
    console.log("error: ", e);
    throw new GetOverviewStatsFailure();
  }
}

export class GetOverviewStatsFailure extends Failure {
  constructor(message = "Unknown error", code = "unknown-error") {
    super(message, code);
  }
}

export default getOverviewStats;
